import type { Messages } from './en';

/**
 * Fase E (paquete CKS): la Plaza de la Libertad / Salón Conmemorativo de Chiang Kai-shek y las aguas termales de Beitou.
 * Las líneas de práctica de inglés se quedan en inglés a propósito.
 */
export const esCks: Pick<Messages, 'cks'> = {
  cks: {
    mapNames: { cks: 'Plaza de la Libertad', beitou: 'Aguas termales de Beitou' },
    zones: {
      ck_hall: 'Salón Conmemorativo de Chiang Kai-shek', ck_gate: 'Puerta de la Plaza de la Libertad', ck_theater: 'Teatro Nacional', ck_concert: 'Sala Nacional de Conciertos',
      ck_guanghua: 'Estanque Guanghua', ck_yunhan: 'Estanque Yunhan', ck_square: 'Plaza de la Libertad', ck_garden: 'Jardines del Salón Conmemorativo',
      ck_valley: 'Valle Termal', ck_millennium: 'Baños Termales del Milenio', ck_plumGarden: 'Jardín de los Ciruelos', ck_library: 'Biblioteca de Beitou',
      ck_museum: 'Museo de las Aguas Termales', ck_btStation: 'Estación de Xinbeitou', ck_hotels: 'Hoteles termales', ck_park: 'Parque de Beitou',
    },
    objects: {
      ck_mrt: 'MRT: estación Salón Conmemorativo de CKS', ck_gate: 'Rincón para fotos: Puerta de la Plaza de la Libertad', ck_steps: 'Los 89 escalones', ck_guard: 'Relevo de la guardia',
      ck_hall: 'Placa del Salón Conmemorativo', ck_theater: 'Teatro Nacional', ck_concert: 'Sala Nacional de Conciertos', ck_koi: 'Las koi del estanque Guanghua',
      ck_yunhan: 'Estanque Yunhan', ck_taichi: 'Taichí por la mañana', ck_kite: 'Vendedor de cometas', ck_snack: 'Carrito de pastelitos de huevo', ck_bubble: 'Puesto de té de burbujas',
      ck_corridor: 'Tablón del pasillo', ck_dance: 'Grupo de baile de la tarde', ck_pigeons: 'Palomas',
      ck_btMrt: 'MRT: estación de Xinbeitou', ck_oldStation: 'Antigua estación de Xinbeitou', ck_library: 'Biblioteca Pública de Beitou',
      ck_museum: 'Museo de las Aguas Termales de Beitou', ck_bath: 'Baños Termales del Milenio', ck_footbath: 'Baño de pies gratuito', ck_valley: 'Valle Termal',
      ck_eggs: 'Huevos termales', ck_plum: 'Jardín de los Ciruelos', ck_stream: 'Arroyo de Beitou',
    },
    npc: {
      ck_guard: 'Guardia de honor', ck_guide: 'Guía turística', ck_taichi: 'Maestro de taichí', ck_taichi2: 'Abuela del taichí', ck_kiteUncle: 'El tío de las cometas',
      ck_dancer: 'Jefa del grupo de baile', ck_teacher: 'Profe de excursión', ck_tourist: 'Mochilero', ck_photographer: 'Fotógrafo',
      ck_koiGrandma: 'La abuela de las koi', ck_student: 'Estudiante universitaria', ck_volunteer: 'Guía voluntario',
      ck_librarian: 'Bibliotecaria', ck_attendant: 'Encargado de los baños', ck_eggAuntie: 'La tía de los huevos', ck_hikerA: 'Excursionista', ck_hikerB: 'Abuelo excursionista',
      ck_footGrandpa: 'El abuelo Lin', ck_museumGuide: 'Guía del museo', ck_stationVol: 'Voluntaria de la estación', ck_tourist2: 'Visitante de Japón', ck_kid: 'Xiaoan',
    },
    items: {} as Record<string, { name: string; desc: string }>,

    // --- Comunes ---------------------------------------------------------------------------------
    listen: '🔊 Escuchar otra vez',
    next: '▶ Siguiente',
    ok: '¡Vale!',
    notNow: 'Ahora no',
    learned: '¡Has aprendido algo nuevo! ⭐',
    poor: 'No tienes suficientes monedas.',

    // --- Plaza de la Libertad ------------------------------------------------------------------------
    welcomeCks: '🏯 ¡La Plaza de la Libertad! El salón blanco con el tejado azul está al fondo, subiendo 89 escalones.',
    gate: {
      text: 'La Puerta de la Plaza de la Libertad mide 30 metros de alto y 80 de ancho. Tiene cinco arcos y tejados de tejas azules. Las letras grandes 自由廣場 significan «Plaza de la Libertad».',
      photo: '📸 Pedir a alguien que te haga una foto',
      how: '¿Cómo lo pides? (práctica de inglés)',
      ask: ['Excuse me, could you take a photo of me, please?', 'Hey. Photo. Me. Now.', 'You take picture, I stand.'], // English practice
      wrong: 'La turista duda: “Er… could you ask more politely?”',
      done: '“Say cheese!” 📸 ¡Clic! Una foto perfecta con la puerta detrás.',
    },
    steps: {
      text: 'Hay 89 escalones hasta el Salón Conmemorativo: uno por cada año de la vida de Chiang Kai-shek. ¿Puedes subirlos todos?',
      climb: '🧗 Subir los 89 escalones',
      quiz: '❓ Pregunta',
      counting: '“…eighty-seven, eighty-eight, eighty-nine!” ¡Lo has conseguido! Toda la plaza queda a tus pies, hasta la puerta.', // English practice
      countEn: 'Eighty-seven, eighty-eight, eighty-nine! I made it!', // English practice
      tired: 'Estás demasiado cansado para subir 89 escalones. ¡Descansa un poco primero!',
      q: 'How many steps are there?', // English practice
      a: ['Eighty-nine', 'Forty-two', 'One hundred and one'], // English practice
      right: '¡Correcto! Ochenta y nueve escalones. ¡Qué bien cuentas!',
      wrong: 'Casi. Vuelve a contar… uno, dos, tres…',
    },
    guard: {
      notNow: (next: string) => `La guardia de honor hace el relevo cada hora en punto, de 9 de la mañana a 5 de la tarde. El próximo es a las ${next}. ¡Vuelve entonces!`,
      closed: 'La ceremonia ya ha terminado por hoy. La guardia hace el relevo cada hora de 9:00 a 17:00. ¡Vuelve mañana!',
      intro: '🎖️ ¡Es el relevo de la guardia! El público se queda en silencio. Escucha la explicación:',
      lines: [
        'The new guards march out slowly, step by step. Their boots click on the marble.', // English practice
        'Now they spin their rifles in the air — and they never drop them!', // English practice
        'The old guards and the new guards salute each other.', // English practice
        'The new guards stand completely still. They must not move, talk or smile for one hour!', // English practice
      ],
      done: '¡Menudo espectáculo! Los guardias entrenan durante meses para moverse juntos como si fueran una sola persona.',
      watch: '👀 Mirar',
    },
    hall: {
      text: 'El Salón Conmemorativo Nacional de Chiang Kai-shek mide 70 metros de alto, más o menos como un edificio de 20 pisos. Es de mármol blanco y tiene un tejado de tejas vidriadas azules.',
      q: 'How many sides does the roof have?', // English practice
      a: ['Eight', 'Six', 'Four'], // English practice
      right: '¡Sí! El tejado es octogonal: tiene ocho lados, como una señal de stop.',
      wrong: '¡Vuelve a mirar el tejado y cuenta sus lados!',
    },
    theater: {
      text: 'El Teatro Nacional parece un palacio antiguo, con el tejado dorado y columnas rojas. Aquí se representan óperas, ballets y obras de teatro.',
      ask: '🎭 Preguntar por las entradas',
      vendor: 'Hello! Can I help you?', // English practice
      ok: 'Two tickets for tonight’s show, please.', // English practice
      bad: ['Give ticket.', 'Show. Two. Me.'], // English practice
      done: '“Here you are: two tickets, row C. The show starts at seven thirty!” (Te las guardas de recuerdo).', // English practice
    },
    concert: {
      text: 'La Sala Nacional de Conciertos es la gemela del teatro. Aquí tocan orquestas, coros y pianistas.',
      listen: '🎻 Escuchar desde la puerta',
      heard: 'A través de las puertas oyes violines ensayando. ¡Precioso! 🎶',
      phrase: 'What time does the concert start? — It starts at seven o’clock.', // English practice
    },
    koi: {
      text: 'El estanque Guanghua está lleno de koi: naranjas, blancas, negras y doradas. ¡Algunas koi viven más de 50 años!',
      feed: '🐟 Comprar comida y dar de comer a las koi (5 monedas)',
      fed: '¡Chof! Decenas de koi llegan corriendo y abren la boca: «¡O! ¡O! ¡O!»',
      en: 'Look! The koi are opening their mouths!', // English practice
      already: 'Las koi ya están llenas. ¡No les des demasiado!',
    },
    yunhan: {
      text: 'Yunhan significa «estanque de la Vía Láctea». Lo cruza un puentecito, y las tortugas toman el sol en las rocas.',
      en: 'Look, there’s a turtle on the rock!', // English practice
      q: 'What animal is sunbathing on the rock?', // English practice
      a: ['A turtle', 'A cat', 'A pigeon'], // English practice
      right: '¡Sí, una tortuga! Está calentándose al sol.',
      wrong: 'Mira bien: ¡tiene caparazón!',
    },
    taichi: {
      text: 'Cada mañana, los abuelos y las abuelas practican taichí aquí. Se mueven despacio, como el agua.',
      join: '🧘 Apuntarse',
      en: 'Breathe in… breathe out… move your arms slowly, like a cloud.', // English practice
      done: 'Te sientes tranquilo y despierto. ¡Tienes el cuerpo calentito!',
      later: 'El grupo de taichí se reúne cada mañana de 6 a 10. ¡Ven temprano!',
      already: 'Ya has hecho taichí hoy. ¡Tus piernas te dan las gracias!',
    },
    kite: {
      text: 'El tío de las cometas vende cometas de colores: dragones, mariposas y peces. En la plaza abierta hace mucho viento.',
      buy: '🪁 Comprar y volar una cometa (15 monedas)',
      en: 'The wind is strong today! Let out more string!', // English practice
      done: '¡Tu cometa sube y sube, y su larga cola baila con el viento!',
      words: 'Palabras nuevas: kite · string · tail · wind', // English practice
    },
    snack: {
      vendor: 'Hi! Egg cakes, fresh and warm. What would you like?', // English practice
      ok: 'Can I have a bag of egg cakes, please?', // English practice
      bad: ['Egg cake. Give.', 'I want eat cake now.'], // English practice
      done: 'Here you go! Careful, they’re hot. Enjoy!', // English practice
    },
    bubble: {
      vendor: 'Hello! What can I get you?', // English practice
      ok: 'A medium bubble milk tea with less sugar, please.', // English practice
      bad: ['Tea! Big! Now!', 'Bubble give me.'], // English practice
      done: 'One medium bubble milk tea, less sugar. Here’s your straw!', // English practice
    },
    corridor: {
      intro: 'En un tablón del pasillo de tejas azules hay un test para los visitantes.',
      qs: [
        { q: 'What colour is the Memorial Hall’s roof?', a: ['Blue', 'Red', 'Green'] }, // English practice
        { q: 'Which building has a golden roof?', a: ['The National Theater', 'The MRT station', 'The gate'] }, // English practice
        { q: 'Where do the koi live?', a: ['In the ponds', 'In the trees', 'On the roof'] }, // English practice
      ],
      right: '¡Correcto! ✅',
      wrong: 'Mmm, esa no. ¡Prueba otra vez!',
      done: '¡Has respondido todas las preguntas! El tablón dice: “Thank you for visiting!”',
    },
    dance: {
      text: 'Cada tarde, grupos de bailarines ensayan delante del teatro con un altavoz pequeño que pone música.',
      join: '💃 Unirse al baile',
      en: 'Step to the left, step to the right, and turn around!', // English practice
      done: '¡Has bailado con el grupo! Las señoras te aplauden.',
      later: 'Los grupos de baile vienen por la tarde, de 6 a 10. ¡Ensayan para espectáculos y concursos!',
    },
    pigeons: {
      text: 'Cientos de palomas pasean por la plaza. Cuando algo las asusta, ¡salen volando todas a la vez!',
      watch: '👀 Mirar las palomas',
      en: 'Please don’t chase the pigeons!', // English practice
      done: '¡Flap, flap, flap! Una nube de palomas da una vuelta a la plaza y vuelve a posarse.',
    },

    // --- Beitou ----------------------------------------------------------------------------------------
    welcomeBeitou: '♨️ ¡Bienvenido a Beitou! ¿Hueles las aguas termales? Del arroyo sale vapor.',
    oldStation: {
      text: 'Esta estacioncita de madera se construyó en 1916. En 1988 se la llevaron a otro sitio y en 2017 volvió a casa. Ahora es un pequeño museo.',
      en: 'This station is over one hundred years old.', // English practice
      q: 'What is the old station made of?', // English practice
      a: ['Wood', 'Stone', 'Glass'], // English practice
      right: '¡Sí, de madera! Tablas de madera al estilo japonés y un tejado de tejas.',
      wrong: 'Mira las paredes: tablas marrones…',
    },
    library: {
      text: 'La Biblioteca de Beitou fue la primera biblioteca ecológica de Taiwán. Es de madera, tiene placas solares en el tejado y recoge el agua de la lluvia para las cisternas de los váteres.',
      read: '📖 Leer un rato',
      readDone: 'Lees un libro sobre volcanes bajo el balcón de madera. Muy tranquilo, muy agradable.',
      en: 'Excuse me, where can I find books in English?', // English practice
      reply: '“They’re on the second floor, next to the window.”', // English practice
      ask: '🗣️ Preguntar a la bibliotecaria',
      q: 'What do the solar panels on the roof make?', // English practice
      a: ['Electricity', 'Water', 'Books'], // English practice
      right: '¡Correcto! La luz del sol se convierte en electricidad.',
      wrong: 'Piensa: el sol brilla sobre las placas y produce…',
      quiz: '❓ Pregunta',
    },
    museum: {
      text: 'El Museo de las Aguas Termales se construyó en 1913 como baño público, el más grande de Asia Oriental. Abajo (ladrillo rojo) está el gran baño de estilo romano; arriba (madera), una sala de tatami para descansar.',
      en: 'Is the museum free? — Yes, it’s free!', // English practice
      q: 'What was the museum before?', // English practice
      a: ['A public bathhouse', 'A train station', 'A school'], // English practice
      right: '¡Exacto! La gente venía aquí a bañarse hace más de cien años.',
      wrong: 'Pista: abajo hay un baño enorme…',
    },
    bath: {
      text: 'Los Baños Termales del Milenio son un baño público al aire libre junto al arroyo. Todo el mundo lleva bañador, y las pozas van de templadas a muy calientes.',
      go: '♨️ Bañarse una hora (40 monedas)',
      rule: 'El encargado pregunta: “What must you wear in the pools?”', // English practice
      a: ['A swimsuit', 'My school uniform', 'A raincoat'], // English practice
      wrong: '“No, no! Try again.”',
      done: 'Primero te duchas y luego te metes despacito en la poza templada. Aaah… Todo tu cuerpo se relaja. 😌',
      en: 'Please take a shower before you get in the pool.', // English practice
      already: 'Ya te has bañado hoy. ¡Demasiada agua caliente marea!',
      closed: 'Los Baños Termales del Milenio están cerrados ahora. Abren a las 5:30 de la mañana.',
    },
    footbath: {
      text: '¡Un baño de pies público y gratuito! Siéntate en el borde de madera y mete los pies en el agua calentita.',
      soak: '🦶 Remojar los pies',
      phrases: [
        'The water is hot! Let me put my feet in slowly.', // English practice
        'It’s warm and cosy. I love it!', // English practice
        'Ouch, it’s too hot! Is it forty-two degrees?', // English practice
        'Ahh, my feet feel so good after walking all day.', // English practice
      ],
      done: 'Se te ponen los pies rosados y calentitos. ¡Te sientes descansado! (+energía, +ánimo)',
      already: 'Ya tienes los pies arrugados como pasas. ¡Con una vez al día basta!',
      q: 'The water is 42°C. Is it hot or cold?', // English practice
      a: ['It’s hot', 'It’s cold', 'It’s frozen'], // English practice
      right: '¡Sí, está caliente! Hot · warm · cool · cold.', // English practice
      wrong: 'Mmm, 42 grados es más que tu cuerpo (37)…',
      quiz: '🌡️ Test de temperatura',
    },
    valley: {
      text: 'Valle Termal: ¡el agua verde jade burbujea hasta a 90 °C! El vapor huele a azufre, como a huevos podridos. Antes lo llamaban «el Valle del Infierno».',
      en: 'Danger! Do not touch the water. It’s very hot!', // English practice
      q: 'How hot can the water in Thermal Valley get?', // English practice
      a: ['About ninety degrees', 'About twenty degrees', 'About zero degrees'], // English practice
      right: '¡Sí, casi hirviendo! Por eso hay una barandilla.',
      wrong: 'Vuelve a mirar el vapor y las señales de peligro…',
      fact: 'Aquí crece un mineral verde muy raro llamado piedra de Beitou (hokutolita). ¡Solo se encuentra en dos lugares del mundo!',
    },
    eggs: {
      vendor: 'Hot-spring eggs! Cooked in the hot water. How many would you like?', // English practice
      ok: 'Three eggs, please.', // English practice
      bad: ['Egg. Three. Now.', 'Give me egg you.'], // English practice
      done: 'Here you are, three warm eggs with a little salt. The yolk is soft and creamy!', // English practice
    },
    plum: {
      text: 'El Jardín de los Ciruelos (梅庭) era la casa de verano del calígrafo Yu Youren. La flor del ciruelo es la flor nacional de Taiwán.',
      en: 'The plum blossom is the national flower of Taiwan.', // English practice
      q: 'When do plum flowers bloom?', // English practice
      a: ['In winter', 'In summer', 'Never'], // English practice
      right: '¡Sí! Florecen con el frío del invierno, por eso simbolizan ser valiente y fuerte.',
      wrong: 'Pista: florecen cuando hace frío…',
    },
    stream: {
      text: '¡El agua del arroyo de Beitou está caliente! El agua termal baja desde el Valle Termal, y los días frescos flota vapor por encima.',
      en: 'What’s that smell? — It’s sulphur from the hot springs.', // English practice
    },

    // --- Personajes ------------------------------------------------------------------------------------
    talk: {
      ck_guard: [
        { text: 'Estoy en mi descanso. Dentro del salón estamos quietos una hora entera. ¡Ni moverse, ni sonreír!' },
        { text: 'Nuestros fusiles pesan unos cuatro kilos. Los hacemos girar cada día en los ensayos.' },
        { text: 'Los visitantes me preguntan: “How do you stand so still?” Y yo les digo: “Practice, practice, practice!”', en: 'Practice, practice, practice!' }, // English practice
      ],
      ck_guide: [
        { text: '¡Seguid la bandera amarilla, todos! Por aquí al Salón Conmemorativo.', en: 'Follow me, please! This way.' }, // English practice
        { text: 'El teatro y la sala de conciertos están uno frente al otro, al otro lado de la plaza, como gemelos.' },
        { text: 'Un consejo: de noche el salón es precioso, iluminado de blanco.' },
      ],
      ck_taichi: [
        { text: '¡Buenos días! El taichí me mantiene joven. ¡Tengo ochenta y dos años!', en: 'Good morning! How are you today?' }, // English practice
        { text: 'Dobla un poco las rodillas… empuja el aire despacio, así.' },
      ],
      ck_taichi2: [
        { text: 'Venimos cada mañana a las seis, llueva o haga sol.', en: 'Rain or shine!' }, // English practice
        { text: 'Después del taichí, nos vamos todos a tomar leche de soja y tortitas de huevo. ¡Riquísimo!' },
      ],
      ck_kiteUncle: [
        { text: '¡Corre contra el viento y suelta el hilo poco a poco!', en: 'Let the string out little by little.' }, // English practice
        { text: 'Mi mejor cometa es un dragón de diez metros. ¡Hacen falta tres personas para volarlo!' },
      ],
      ck_dancer: [
        { text: '¡Cinco, seis, siete, ocho! ¡Otra vez, desde el principio!', en: 'Five, six, seven, eight!' }, // English practice
        { text: 'Estamos ensayando para un concurso el mes que viene. ¿Te apuntas?' },
      ],
      ck_teacher: [
        { text: '¡Clase, no os separéis, por favor! ¡No corráis por los escalones!', en: 'Stay together, please!' }, // English practice
        { text: 'Hoy aprendemos sobre la historia de Taiwán y sus edificios.' },
      ],
      ck_tourist: [
        { text: '¡Hola! Soy de Canadá. ¿Dónde está la estación del MRT?', en: 'Excuse me, where is the MRT station?' }, // English practice
        { text: '¿Es gratis visitar el Salón Conmemorativo? ¡Sí, lo es! ¡Gracias!', en: 'Is it free to visit?' }, // English practice
        { text: 'Me encanta el té de burbujas. ¡En inglés decimos “bubble tea” o “boba”!' }, // English practice
      ],
      ck_photographer: [
        { text: 'La mejor foto se hace desde aquí: la puerta entera en el encuadre, con el cielo azul detrás.' },
        { text: '¡Sonríe y di “cheese”!', en: 'Smile and say cheese!' }, // English practice
      ],
      ck_koiGrandma: [
        { text: 'Esa koi naranja tan grande se llama Suerte. ¡Le doy de comer desde hace diez años!' },
        { text: 'Colores de koi: naranja, blanco, negro, dorado. ¿Cuál es tu favorito?', en: 'Which colour is your favourite?' }, // English practice
      ],
      ck_student: [
        { text: 'Estudio en el pasillo. Es fresco y tranquilo, y corre la brisa.' },
        { text: 'Estoy practicando inglés para un examen. “How long have you lived in Taipei?”', en: 'How long have you lived in Taipei?' }, // English practice
      ],
      ck_volunteer: [
        { text: '¡Bienvenido! El salón abre a las nueve y cierra a las seis.', en: 'The hall opens at nine and closes at six.' }, // English practice
        { text: 'Las tejas azules y el mármol blanco hacen que el edificio parezca tranquilo y majestuoso.' },
      ],
      ck_librarian: [
        { text: '¡Chis! Esto es una biblioteca. Habla bajito, por favor.', en: 'Please speak quietly.' }, // English practice
        { text: 'Los grandes balcones de madera tapan el sol, así que necesitamos menos aire acondicionado.' },
      ],
      ck_attendant: [
        { text: '¡Primero ducha, luego bañador, y nada de toallas en el agua!', en: 'Please shower first.' }, // English practice
        { text: 'La poza más caliente está arriba del todo, a unos cuarenta y cuatro grados. ¡Cuidado!' },
      ],
      ck_eggAuntie: [
        { text: 'Mis huevos se cuecen en agua termal. ¡Se notan los minerales!' },
        { text: '¿Cuántos huevos? Uno, dos, tres… ¡Sé contar en inglés!', en: 'How many eggs would you like?' }, // English practice
      ],
      ck_hikerA: [
        { text: 'He bajado andando desde Yangmingshan. ¡Allí arriba hay volcanes!' },
        { text: 'Cuando vayas de excursión, no te olvides del agua y de una gorra.', en: 'Don’t forget your water bottle!' }, // English practice
      ],
      ck_hikerB: [
        { text: '¡Setenta años y todavía subo a la montaña cada fin de semana!' },
        { text: 'Después de una excursión, un baño de pies es el mejor premio.', en: 'What a beautiful day for a walk!' }, // English practice
      ],
      ck_footGrandpa: [
        { text: '¡Ven, siéntate! Hoy el agua está perfecta. A unos cuarenta grados.', en: 'The water is just right.' }, // English practice
        { text: 'Cuando yo era pequeño, aquí las aguas termales estaban llenas de vapor y de canciones.' },
        { text: 'Hot, warm, cool, cold. ¡Dilo conmigo!', en: 'Hot, warm, cool, cold.' }, // English practice
      ],
      ck_museumGuide: [
        { text: 'Quítate los zapatos antes de subir a la sala de tatami.', en: 'Please take off your shoes.' }, // English practice
        { text: 'Las vidrieras de abajo tienen más de cien años.' },
      ],
      ck_stationVol: [
        { text: 'La estación antigua tenía un trenecito que traía a los bañistas desde Beitou hasta aquí.' },
        { text: 'Mira la ventana del tejado. Deja entrar la luz en la sala de espera.' },
      ],
      ck_tourist2: [
        { text: '¡Konnichiwa! Beitou parece un pueblo termal de Japón.' },
        { text: 'Siempre pruebo los huevos termales. ¡Están buenísimos!', en: 'They are so good!' }, // English practice
      ],
      ck_kid: [
        { text: '¡El agua del arroyo está calentita! Mamá dice que no meta las manos.' },
        { text: '¿Por qué huele a huevo? ¡Es el azufre! Hoy he aprendido esa palabra.', en: 'It smells like eggs!' }, // English practice
      ],
    } as Record<string, { text: string; en?: string }[]>,
  },
};
